import _defineProperty from "/drone/src/node_modules/@babel/runtime/helpers/esm/defineProperty.js";
function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }
function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }
import { tracingContextFromHeaders, logger, dropUndefinedKeys, addNonEnumerableProperty } from '@sentry/utils';
import { DEBUG_BUILD } from '../debug-build.js';
import { getCurrentHub, runWithAsyncContext, getIsolationScope } from '../hub.js';
import { spanToJSON, spanIsSampled, spanTimeInputToSeconds } from '../utils/spanUtils.js';
import './errors.js';
import './spanstatus.js';
import { getDynamicSamplingContextFromSpan } from './dynamicSamplingContext.js';
import { getCurrentScope, withScope } from '../exports.js';
import { handleCallbackErrors } from '../utils/handleCallbackErrors.js';
import { hasTracingEnabled } from '../utils/hasTracingEnabled.js';

/**
 * Wraps a function with a transaction/span and finishes the span after the function is done.
 *
 * Note that if you have not enabled tracing extensions via `addTracingExtensions`
 * or you didn't set `tracesSampleRate`, this function will not generate spans
 * and the `span` returned from the callback will be undefined.
 *
 * This function is meant to be used internally and may break at any time. Use at your own risk.
 *
 * @internal
 * @private
 *
 * @deprecated Use `startSpan` instead.
 */
function trace(context, callback) {
  var onError = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : function () {};
  var afterFinish = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : function () {};
  // eslint-disable-next-line deprecation/deprecation
  var hub = getCurrentHub();
  var scope = getCurrentScope();
  // eslint-disable-next-line deprecation/deprecation
  var parentSpan = scope.getSpan();
  var spanContext = normalizeContext(context);
  var activeSpan = createChildSpanOrTransaction(hub, {
    parentSpan: parentSpan,
    spanContext: spanContext,
    forceTransaction: false,
    scope: scope
  });

  // eslint-disable-next-line deprecation/deprecation
  scope.setSpan(activeSpan);
  return handleCallbackErrors(function () {
    return callback(activeSpan);
  }, function (error) {
    activeSpan && activeSpan.setStatus('internal_error');
    onError(error, activeSpan);
  }, function () {
    activeSpan && activeSpan.end();
    // eslint-disable-next-line deprecation/deprecation
    scope.setSpan(parentSpan);
    afterFinish();
  });
}

/**
 * Wraps a function with a transaction/span and finishes the span after the function is done.
 * The created span is the active span and will be used as parent by other spans created inside the function
 * and can be accessed via `Sentry.getSpan()`, as long as the function is executed while the scope is active.
 *
 * If you want to create a span that is not set as active, use {@link startInactiveSpan}.
 *
 * Note that if you have not enabled tracing extensions via `addTracingExtensions`
 * or you didn't set `tracesSampleRate`, this function will not generate spans
 * and the `span` returned from the callback will be undefined.
 */
function startSpan(context, callback) {
  var spanContext = normalizeContext(context);
  return runWithAsyncContext(function () {
    return withScope(context.scope, function (scope) {
      // eslint-disable-next-line deprecation/deprecation
      var hub = getCurrentHub();
      // eslint-disable-next-line deprecation/deprecation
      var parentSpan = scope.getSpan();
      var shouldSkipSpan = context.onlyIfParent && !parentSpan;
      var activeSpan = shouldSkipSpan ? undefined : createChildSpanOrTransaction(hub, {
        parentSpan: parentSpan,
        spanContext: spanContext,
        forceTransaction: context.forceTransaction,
        scope: scope
      });
      return handleCallbackErrors(function () {
        return callback(activeSpan);
      }, function () {
        // Only update the span status if it hasn't been changed yet
        if (activeSpan) {
          var _spanToJSON = spanToJSON(activeSpan),
            status = _spanToJSON.status;
          if (!status || status === 'ok') {
            activeSpan.setStatus('internal_error');
          }
        }
      }, function () {
        return activeSpan && activeSpan.end();
      });
    });
  });
}

/**
 * @deprecated Use {@link startSpan} instead.
 */
var startActiveSpan = startSpan;

/**
 * Similar to `Sentry.startSpan`. Wraps a function with a transaction/span, but does not finish the span
 * after the function is done automatically. You'll have to call `span.end()` manually.
 *
 * The created span is the active span and will be used as parent by other spans created inside the function
 * and can be accessed via `Sentry.getActiveSpan()`, as long as the function is executed while the scope is active.
 *
 * Note that if you have not enabled tracing extensions via `addTracingExtensions`
 * or you didn't set `tracesSampleRate`, this function will not generate spans
 * and the `span` returned from the callback will be undefined.
 */
function startSpanManual(context, callback) {
  var spanContext = normalizeContext(context);
  return runWithAsyncContext(function () {
    return withScope(context.scope, function (scope) {
      // eslint-disable-next-line deprecation/deprecation
      var hub = getCurrentHub();
      // eslint-disable-next-line deprecation/deprecation
      var parentSpan = scope.getSpan();
      var shouldSkipSpan = context.onlyIfParent && !parentSpan;
      var activeSpan = shouldSkipSpan ? undefined : createChildSpanOrTransaction(hub, {
        parentSpan: parentSpan,
        spanContext: spanContext,
        forceTransaction: context.forceTransaction,
        scope: scope
      });
      function finishAndSetSpan() {
        activeSpan && activeSpan.end();
      }
      return handleCallbackErrors(function () {
        return callback(activeSpan, finishAndSetSpan);
      }, function () {
        // Only update the span status if it hasn't been changed yet, and the span is not yet finished
        if (activeSpan && activeSpan.isRecording()) {
          var _spanToJSON2 = spanToJSON(activeSpan),
            status = _spanToJSON2.status;
          if (!status || status === 'ok') {
            activeSpan.setStatus('internal_error');
          }
        }
      });
    });
  });
}

/**
 * Creates a span. This span is not set as active, so will not get automatic instrumentation spans
 * as children or be able to be accessed via `Sentry.getSpan()`.
 *
 * If you want to create a span that is set as active, use {@link startSpan}.
 *
 * Note that if you have not enabled tracing extensions via `addTracingExtensions`
 * or you didn't set `tracesSampleRate` or `tracesSampler`, this function will not generate spans
 * and the `span` returned from the callback will be undefined.
 */
function startInactiveSpan(context) {
  if (!hasTracingEnabled()) {
    return undefined;
  }
  var spanContext = normalizeContext(context);
  // eslint-disable-next-line deprecation/deprecation
  var hub = getCurrentHub();
  var parentSpan = context.scope ?
  // eslint-disable-next-line deprecation/deprecation
  context.scope.getSpan() : getActiveSpan();
  var shouldSkipSpan = context.onlyIfParent && !parentSpan;
  if (shouldSkipSpan) {
    return undefined;
  }
  var scope = context.scope || getCurrentScope();

  // Even though we don't actually want to make this span active on the current scope,
  // we need to make it active on a temporary scope that we use for event processing
  // as otherwise, it won't pick the correct span for the event when processing it
  var temporaryScope = scope.clone();
  return createChildSpanOrTransaction(hub, {
    parentSpan: parentSpan,
    spanContext: spanContext,
    forceTransaction: context.forceTransaction,
    scope: temporaryScope
  });
}

/**
 * Returns the currently active span.
 */
function getActiveSpan() {
  // eslint-disable-next-line deprecation/deprecation
  return getCurrentScope().getSpan();
}
var continueTrace = function continueTrace(_ref, callback) {
  var sentryTrace = _ref.sentryTrace,
    baggage = _ref.baggage;
  // TODO(v8): Change this function so it doesn't do anything besides setting the propagation context on the current scope:
  /*
    return withScope((scope) => {
      const propagationContext = propagationContextFromHeaders(sentryTrace, baggage);
      scope.setPropagationContext(propagationContext);
      return callback();
    })
  */

  var currentScope = getCurrentScope();

  // eslint-disable-next-line deprecation/deprecation
  var _tracingContextFromHe = tracingContextFromHeaders(sentryTrace, baggage),
    traceparentData = _tracingContextFromHe.traceparentData,
    dynamicSamplingContext = _tracingContextFromHe.dynamicSamplingContext,
    propagationContext = _tracingContextFromHe.propagationContext;
  currentScope.setPropagationContext(propagationContext);
  if (DEBUG_BUILD && traceparentData) {
    logger.log("[Tracing] Continuing trace ".concat(traceparentData.traceId, "."));
  }
  var transactionContext = _objectSpread(_objectSpread({}, traceparentData), {}, {
    metadata: dropUndefinedKeys({
      dynamicSamplingContext: dynamicSamplingContext
    })
  });
  if (!callback) {
    return transactionContext;
  }
  return runWithAsyncContext(function () {
    return callback(transactionContext);
  });
};
function createChildSpanOrTransaction(
// eslint-disable-next-line deprecation/deprecation
hub, _ref2) {
  var parentSpan = _ref2.parentSpan,
    spanContext = _ref2.spanContext,
    forceTransaction = _ref2.forceTransaction,
    scope = _ref2.scope;
  if (!hasTracingEnabled()) {
    return undefined;
  }
  var isolationScope = getIsolationScope();
  var span;
  if (parentSpan && !forceTransaction) {
    // eslint-disable-next-line deprecation/deprecation
    span = parentSpan.startChild(spanContext);
  } else if (parentSpan) {
    // If we forced a transaction but have a parent span, make sure to continue from the parent span, not the scope
    var dsc = getDynamicSamplingContextFromSpan(parentSpan);
    var _parentSpan$spanConte = parentSpan.spanContext(),
      traceId = _parentSpan$spanConte.traceId,
      parentSpanId = _parentSpan$spanConte.spanId;
    var sampled = spanIsSampled(parentSpan);

    // eslint-disable-next-line deprecation/deprecation
    span = hub.startTransaction(_objectSpread(_objectSpread({
      traceId: traceId,
      parentSpanId: parentSpanId,
      parentSampled: sampled
    }, spanContext), {}, {
      metadata: _objectSpread({
        dynamicSamplingContext: dsc
      }, spanContext.metadata)
    }));
  } else {
    var _isolationScope$getPr = _objectSpread(_objectSpread({}, isolationScope.getPropagationContext()), scope.getPropagationContext()),
      _traceId = _isolationScope$getPr.traceId,
      _dsc = _isolationScope$getPr.dsc,
      _parentSpanId = _isolationScope$getPr.parentSpanId,
      _sampled = _isolationScope$getPr.sampled;

    // eslint-disable-next-line deprecation/deprecation
    span = hub.startTransaction(_objectSpread(_objectSpread({
      traceId: _traceId,
      parentSpanId: _parentSpanId,
      parentSampled: _sampled
    }, spanContext), {}, {
      metadata: _objectSpread({
        dynamicSamplingContext: _dsc
      }, spanContext.metadata)
    }));
  }

  // We always set this as active span on the scope
  // In the case of this being an inactive span, we ensure to pass a detached scope in here in the first place
  // But by having this here, we can ensure that the lookup through `getCapturedScopesOnSpan` results in the correct scope & span combo
  // eslint-disable-next-line deprecation/deprecation
  scope.setSpan(span);
  setCapturedScopesOnSpan(span, scope, isolationScope);
  return span;
}

/**
 * This converts StartSpanOptions to TransactionContext.
 * For the most part (for now) we accept the same options,
 * but some of them need to be transformed.
 *
 * Eventually the StartSpanOptions will be more aligned with OpenTelemetry.
 */
function normalizeContext(context) {
  if (context.startTime) {
    var ctx = _objectSpread({}, context);
    ctx.startTimestamp = spanTimeInputToSeconds(context.startTime);
    delete ctx.startTime;
    return ctx;
  }
  return context;
}
var SCOPE_ON_START_SPAN_FIELD = '_sentryScope';
var ISOLATION_SCOPE_ON_START_SPAN_FIELD = '_sentryIsolationScope';
function setCapturedScopesOnSpan(span, scope, isolationScope) {
  if (span) {
    addNonEnumerableProperty(span, ISOLATION_SCOPE_ON_START_SPAN_FIELD, isolationScope);
    addNonEnumerableProperty(span, SCOPE_ON_START_SPAN_FIELD, scope);
  }
}

/**
 * Grabs the scope and isolation scope off a span that were active when the span was started.
 */
function getCapturedScopesOnSpan(span) {
  return {
    scope: span[SCOPE_ON_START_SPAN_FIELD],
    isolationScope: span[ISOLATION_SCOPE_ON_START_SPAN_FIELD]
  };
}
export { continueTrace, getActiveSpan, getCapturedScopesOnSpan, startActiveSpan, startInactiveSpan, startSpan, startSpanManual, trace };